/**
 * useCampaignFinalizedReviewControls — write-side companion for the read-only
 * Script / Content review side panels on the Confirmed (Finalized) tab.
 *
 * Mirrors useCampaignReviewControls (which targets the LIVE in-workflow
 * Script/Content review tabs) but writes the dedicated finalized panel values:
 *
 *   openFinalized(row, 'script')   → ?panel=script-finalized&creator=<username>
 *   openFinalized(row, 'content')  → ?panel=content-finalized&creator=<username>
 *   closeFinalized()               → clears panel/creator/sub/thread
 *
 * Distinct enum values (not a ?mode=ro flag on the live panel) so the URL self-
 * describes the surface and the share/refresh behavior is unambiguous: a link
 * to a finalized read-only artifact is *semantically different* from a link to
 * a live in-workflow review (the latter has actions; the former is frozen).
 *
 * Keyless (shadow) creators open locally only — no `?creator=` write — same
 * guard as the other controls hooks.
 */
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

export type FinalizedReviewMode = 'script' | 'content';

type FinalizedRow = {
  id: string;
  platform?: { username?: string };
};

type SetReadOnlyDialog<TRow extends FinalizedRow> = (
  next: { creator: TRow; mode: FinalizedReviewMode } | null,
) => void;

// Generic over the stored row so callers can retain their full entry type
// (e.g. RosterEntryFinalized) end-to-end; the hook itself only needs the
// `id` / `platform.username` fields declared by FinalizedRow.
export function useCampaignFinalizedReviewControls<TRow extends FinalizedRow = FinalizedRow>(
  setReadOnlyDialog: SetReadOnlyDialog<TRow>,
) {
  const navigate = useNavigate();

  /**
   * Open the read-only script or content review panel for `row`.
   *
   * Sets local `readOnlyDialog` instantly and writes
   * `?panel=script-finalized|content-finalized&creator=<username>` so a refresh
   * restores the same panel. Keyless creators open locally only.
   */
  const openFinalized = useCallback(
    (row: TRow, mode: FinalizedReviewMode) => {
      setReadOnlyDialog({ creator: row, mode });

      const username = row.platform?.username?.trim();
      if (!username) return;

      const panel = mode === 'script' ? 'script-finalized' : 'content-finalized';
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          panel,
          creator: username,
          // sub belongs to insights; clear it so an existing ?sub doesn't leak.
          sub: undefined,
        })),
      } as never);
    },
    [navigate, setReadOnlyDialog],
  );

  /**
   * Close the read-only panel. Clears local state AND panel/creator/sub/thread
   * URL params. Preserves tab/pill/page so the user stays where they were.
   */
  const closeFinalized = useCallback(() => {
    setReadOnlyDialog(null);

    navigate({
      to: '.',
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        panel: undefined,
        creator: undefined,
        sub: undefined,
        thread: undefined,
      })),
    } as never);
  }, [navigate, setReadOnlyDialog]);

  return { openFinalized, closeFinalized };
}
